import React, { Component, PropTypes } from 'react';
import { compose, graphql } from 'react-apollo';
import update from 'immutability-helper';
import { connect } from 'react-redux';

import { addUserToEventSaves, removeUserFromEventSaves } from '../../state/actions';
import { formatNumber } from '../../utils/formats';
import { trackEventSaved, trackEventUnsaved } from '../../utils/tracking';
import authService from '../../state/services/auth';
import { GQLQueryPropType, eventFullPropTypeShape } from '../../proptypes';
import {
  SaveEventMutation,
  getSaveEventMutationOptimisticResponse,
  UnsaveEventMutation,
  getUnsaveEventMutationOptimisticResponse,
  UserQueryPublicSavedEvents,
} from '../../state/services/gql/user';

import HeartButton from '../../components/HeartButton';

class HeartContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loggedIn: authService.loggedIn(),
      tooltipVisible: false,
    };
  }

  isSaved = () => {
    const { data } = this.props;
    const savedEvents = data && data.profile && data.profile.savedEvents;
    if (savedEvents) {
      return savedEvents.map(savedEvent =>
        savedEvent.event.id).includes(this.props.id.toString());
    }
    return false;
  }

  handleClick = ({ target }) => {
    if (this.isSaved()) {
      this.unsaveEvent(target.id.toString());
    } else {
      this.saveEvent(target.id.toString());
    }
  }

  saveEvent(id) {
    const userId = this.props.data.profile.id;
    this.props.onSave({
      variables: {
        id,
      },
      optimisticResponse: getSaveEventMutationOptimisticResponse(id, this.props.data.profile),
    }).then(() => {
      trackEventSaved(this.props.trackingObject);
    }).catch(() => {
      this.props.dispatchRemoveUserFromEventSaves(id, userId);
    });
    this.props.dispatchAddUserToEventSaves(id, userId);
  }

  unsaveEvent(id) {
    const userId = this.props.data.profile.id;
    this.props.onUnsave({
      variables: {
        id,
      },
      optimisticResponse: getUnsaveEventMutationOptimisticResponse(id, this.props.data.profile),
    }).then(() => {
      trackEventUnsaved(this.props.trackingObject);
    }).catch(() => {
      this.props.dispatchAddUserToEventSaves(id, userId);
    });
    this.props.dispatchRemoveUserFromEventSaves(id, userId);
  }

  setTooltip = (isVisible) => {
    this.setState({
      tooltipVisible: isVisible,
    });
  }

  handleSaveEventMouseEnter = () => {
    if (!this.props.noTooltip) {
      this.setTooltip(true);
    }
  }

  handleSaveEventMouseLeave = () => {
    if (!this.props.noTooltip) {
      this.setTooltip(false);
    }
  }

  render() {
    const { children } = this.props;
    return (
      <HeartButton
        onClick={this.handleClick}
        onMouseEnter={this.handleSaveEventMouseEnter}
        onMouseLeave={this.handleSaveEventMouseLeave}
        {...this.props}
        {...this.state}
        saved={this.isSaved()}
      >{children}</HeartButton>
    );
  }
}

HeartContainer.propTypes = {
  /**
   * Boolean whether booking is enabled.
   */
  bookingPartner: PropTypes.shape(),
  /**
   * Children contents of button.
   */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  /**
   * The amount of time the event has been saved.
   */
  count: PropTypes.number,
  /**
   * Data object returned from GraphQL query.
   */
  data: GQLQueryPropType,
  /**
   * Boolean used in order to not render tooltip on hovering over button.
   */
  noTooltip: PropTypes.bool,
  /**
   * Callback function when user clicks on Save Event button.
   */
  onSave: PropTypes.func,
  /**
   * Callback function when user unsaves event.
   */
  onUnsave: PropTypes.func,
  /**
   * Callback function to update store when user saves event.
   */
  dispatchAddUserToEventSaves: PropTypes.func,
  /**
   * Callback function to update store when user unsaves event.
   */
  dispatchRemoveUserFromEventSaves: PropTypes.func,
  /**
   * Event object to be tracked on save/unsave using Segment.
   */
  trackingObject: PropTypes.shape({
    id: eventFullPropTypeShape.id,
    title: eventFullPropTypeShape.title,
    tags: eventFullPropTypeShape.notifications,
    organiser: eventFullPropTypeShape.organiser.title,
    startDate: eventFullPropTypeShape.startDate,
  }).isRequired,
  /**
   * The amount of time the event has been viewed.
   */
  views: PropTypes.number,
};

HeartContainer.defaultProps = {
  bookingPartner: undefined,
  count: 0,
  data: {},
  noTooltip: undefined,
  onSave: () => {},
  onUnsave: () => {},
  dispatchAddUserToEventSaves: () => {},
  dispatchRemoveUserFromEventSaves: () => {},
  views: 0,
};

// Shorthand object definition, maps action creator to dispatch
export default (props) => {
  const mapDispatchToProps = {
    dispatchAddUserToEventSaves: addUserToEventSaves,
    dispatchRemoveUserFromEventSaves: removeUserFromEventSaves,
  };

  // Wrap in [react-redux connect](https://github.com/reactjs/react-redux/blob/master/docs/api.md#connect)
  const HeartContainerWithRedux = connect(null, mapDispatchToProps)(HeartContainer);  // eslint-disable-line no-class-assign, max-len

  // GraphQL mutations and query are only needed when logged in.
  // TODO: This should be another layer of abstraction/container.
  const HeartContainerWithGraphQL = compose(
    graphql(SaveEventMutation, { name: 'onSave' }),
    graphql(UnsaveEventMutation, { name: 'onUnsave' }),
    graphql(
      UserQueryPublicSavedEvents,
      {
        options: { variables: { id: authService.getUserCookie() } },
        skip: !authService.getUserCookie(),
      },
    ),
  )(HeartContainerWithRedux);

  return <HeartContainerWithGraphQL {...props} />;
};



// WEBPACK FOOTER //
// ./app/src/containers/HeartButton/index.jsx